import { inject, Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { setCssProperty } from '../../../cross-team-shared/misc-utils';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { VectorRendererComponent } from './vector-renderer.component';

const DEFAULT_STROKE_OPACITY = 0.6;

@Injectable()
export class VectorRendererCssPropertyService {
  private store = inject(AbstractSensorContentStore2D);

  manageCssProperties(vectorRenderer: VectorRendererComponent) {
    this.setUpSubpointDisplayCssSubscriptions(vectorRenderer);
    this.setUpSelectedToolAndShowSubpointsCssSubscriptions(vectorRenderer);
    this.setUpSelectedToolCssSubscriptions(vectorRenderer);
    this.setUpScaleCssSubscriptions(vectorRenderer);
    this.setUpLayerOpacityCssSubscriptions(vectorRenderer);
  }

  private setUpSubpointDisplayCssSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    vectorRenderer.subscribeTo(
      combineLatest([
        this.store('layerSettings')('alwaysShowSubpoints').$,
        this.store('editMode').$.pipe(distinctUntilChanged()),
      ]),
      ([alwaysShowSubpoints, editMode]) => {
        if (alwaysShowSubpoints) {
          this.setCssProperty('--h3-subpoint-display', null, vectorRenderer);
        } else {
          const none = editMode === 'none';
          this.setCssProperty(
            '--h3-subpoint-display',
            none ? 'none' : null,
            vectorRenderer,
          );
        }
      },
    );
  }

  private setUpSelectedToolCssSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    const selectedTool$ = this.store('selectedTool').$.pipe(
      distinctUntilChanged(),
    );

    vectorRenderer.subscribeTo(selectedTool$, (activeTool) => {
      const emphasizeOnHover = activeTool === 'Edit' || activeTool === 'Select';

      this.setCssProperty(
        '--h3-hover-subpoint-radius-multiplier',
        emphasizeOnHover ? 1.3 : 1,
        vectorRenderer,
      );
    });
  }

  // this logic cannot be tested because it's impossible to trigger hover from
  // tests (specifically :hover in css)
  private setUpSelectedToolAndShowSubpointsCssSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    const emphasizeOnHover$ = combineLatest([
      this.store('layerSettings')('alwaysShowSubpoints').$,
      this.store('selectedTool').$,
    ]).pipe(
      map(
        ([alwaysShowSubpoints, activeTool]) =>
          alwaysShowSubpoints &&
          (activeTool === 'Edit' || activeTool === 'Select'),
      ),
      distinctUntilChanged(),
    );

    vectorRenderer.subscribeTo(emphasizeOnHover$, (emphasizeOnHover) => {
      this.setCssProperty(
        '--h3-hover-outline-width-multiplier',
        emphasizeOnHover ? 2 : 1,
        vectorRenderer,
      );
      this.setCssProperty(
        '--h3-hover-opacity',
        emphasizeOnHover ? 1 : DEFAULT_STROKE_OPACITY,
        vectorRenderer,
      );
    });
  }

  private setUpScaleCssSubscriptions(vectorRenderer: VectorRendererComponent) {
    vectorRenderer.subscribeTo(
      combineLatest([vectorRenderer.scale$, this.store('pixelSize').$]),
      ([scale, pixelSize]) => {
        const halfPixelSize = pixelSize / 2;
        this.setCssProperty(
          '--h3-interactive-border-line-width',
          Math.max(9 / scale, pixelSize),
          vectorRenderer,
        );
        this.setCssProperty(
          '--h3-line-width',
          Math.max(3 / scale, pixelSize),
          vectorRenderer,
        );
        this.setCssProperty(
          '--h3-screen-pixel-width',
          1 / scale,
          vectorRenderer,
        );
        this.setCssProperty('--h3-pixel-size', pixelSize, vectorRenderer);
        this.setCssProperty(
          '--h3-subpoint-radius',
          Math.max(5 / scale, halfPixelSize),
          vectorRenderer,
        );
      },
    );
  }

  private setUpLayerOpacityCssSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    vectorRenderer.subscribeTo(
      combineLatest([
        this.store('layerSettings')('hideOccludedShapes').$,
        this.store('layerSettings')('opacity').$,
      ]),
      ([hideOccludedShapes, opacity]) => {
        this.setCssProperty(
          '--h3-layer-opacity',
          hideOccludedShapes ? 1 : opacity / 100,
          vectorRenderer,
        );
      },
    );
  }

  private setCssProperty(
    name: string,
    value: string | number | null,
    vectorRenderer: VectorRendererComponent,
  ) {
    setCssProperty(vectorRenderer.elementRef, name, value);
  }
}
